import { Button } from '@heidi/design-system/button';
import { TypographyH2, TypographyP2 } from '@heidi/design-system/typography';

import { Row } from './VisualDxDiagnosticFlow.atoms';
import {
  AGE_BAND_OPTIONS,
  MORPHOLOGY_OPTIONS,
  ONSET_OPTIONS,
  REGION_OPTIONS,
  type AgeBand,
  type BodyAspect,
  type Morphology,
  type OnsetSpeed,
  type Region,
} from './VisualDxDiagnosticFlow.constants';
import { dlClass, stepHeadingWrapClass } from './VisualDxDiagnosticFlow.styles';

/**
 * Post-submit summary card shown while the LLM is generating the ranked
 * differential. Distinct from a "review" step — the demo has no review
 * step; the user clicks "Build differential" on the morphology screen
 * and the next chunk to arrive is the ranked-differential widget.
 *
 * Lives in its own file (rather than inside ``parts.tsx``) so the input-
 * step components stay under the 300-line cap; the label-lookup helpers
 * are colocated here because the summary is the only consumer.
 */

const labelForRegion = (id: Region): string =>
  REGION_OPTIONS.find((opt) => opt.id === id)?.label ?? id;

const labelForMorphology = (id: Morphology): string =>
  MORPHOLOGY_OPTIONS.find((opt) => opt.id === id)?.label ?? id;

const labelForAgeBand = (id: AgeBand): string =>
  AGE_BAND_OPTIONS.find((opt) => opt.id === id)?.label ?? id;

const labelForOnset = (id: OnsetSpeed): string =>
  ONSET_OPTIONS.find((opt) => opt.id === id)?.label ?? id;

export interface ReviewProps {
  bodyAspect: BodyAspect;
  regions: Region[];
  morphology: Morphology[];
  ageBand: AgeBand | null;
  exactYears: string;
  onset: OnsetSpeed | null;
}

const buildReviewRows = (props: ReviewProps): { label: string; value: string }[] => {
  const ageValue =
    props.exactYears !== ''
      ? `${props.exactYears} y${
          props.ageBand ? ` (${labelForAgeBand(props.ageBand)})` : ''
        }`
      : props.ageBand
        ? labelForAgeBand(props.ageBand)
        : '—';
  return [
    {
      label: 'Body aspect',
      value: props.bodyAspect === 'front' ? 'Front' : 'Back',
    },
    {
      label: 'Regions',
      value: props.regions.length ? props.regions.map(labelForRegion).join(', ') : '—',
    },
    {
      label: 'Findings',
      value: props.morphology.length
        ? props.morphology.map(labelForMorphology).join(', ')
        : '—',
    },
    { label: 'Age', value: ageValue },
    { label: 'Onset', value: props.onset ? labelForOnset(props.onset) : '—' },
  ];
};

export const ReviewSummary = ({
  onReset,
  skipped,
  ...props
}: ReviewProps & { onReset?: () => void; skipped: boolean }) => (
  <div>
    <div className={stepHeadingWrapClass}>
      <TypographyH2 className="mb-1.5 text-text-primary">
        {skipped ? 'Skipped to results' : 'Submitted'}
      </TypographyH2>
      <TypographyP2 className="text-text-secondary">
        {skipped
          ? 'Building the ranked differential from the partial info available.'
          : 'Selections sent to the assistant — building the ranked differential now.'}
      </TypographyP2>
    </div>
    <dl className={dlClass}>
      {buildReviewRows(props).map((row) => (
        <Row key={row.label} label={row.label} value={row.value} />
      ))}
    </dl>
    {onReset ? (
      <Button variant="outline" size="sm" onClick={onReset} className="mt-4">
        Restart
      </Button>
    ) : null}
  </div>
);
